import React, { Suspense, useMemo, useState } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { ListKeepAliveProps } from './ListKeepAlive.props';
import { ListKeepAliveContext } from './ListKeepAlive.context';

/**
 * Layout route that keeps a list page mounted underneath its child pages
 * (view / edit / new), the same way the resource request app does.
 *
 * While a child route is showing, the list is hidden with CSS (`.list-keep-alive--hidden`
 * in index.css) instead of being unmounted, so going back shows it exactly as it was
 * left — scroll position, sort, filters — without reloading. Saves made in the
 * meantime reach the table through the `listRefresh` store slice.
 */
const ListKeepAliveView: React.FC<ListKeepAliveProps> = ({
  listPath,
  children,
  fallback = null
}) => {
  const isShowing = useMatch(`${process.env.REACT_APP_APP_URL_PATH}/${listPath}`) !== null;

  // Mount the list the first time it is visited, not when a child page is opened directly.
  const [hasShown, setHasShown] = useState(isShowing);
  if (isShowing && !hasShown) setHasShown(true);

  const state = useMemo(() => ({ isShowing }), [isShowing]);

  return (
    <>
      {hasShown && (
        <div className={isShowing ? 'list-keep-alive' : 'list-keep-alive list-keep-alive--hidden'}>
          <ListKeepAliveContext.Provider value={state}>{children}</ListKeepAliveContext.Provider>
        </div>
      )}
      {/* Own boundary: a child page that is still loading must not suspend (and so hide) the list. */}
      <Suspense fallback={fallback}>
        <Outlet />
      </Suspense>
    </>
  );
};

export default ListKeepAliveView;
